import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Paper,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as ResetIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { 
  whatsappTemplatesService, 
  WhatsAppTemplate, 
  WhatsAppTemplateType 
} from '../../services/whatsappTemplatesService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const WhatsAppTemplateEditor: React.FC = () => {
  const [templates, setTemplates] = useState<Record<WhatsAppTemplateType, WhatsAppTemplate>>({} as any);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const templateKeys: WhatsAppTemplateType[] = ['deviceReceived', 'deviceInProgress', 'deviceCompleted', 'deviceUrgent'];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const loadedTemplates = await whatsappTemplatesService.getTemplates();
      setTemplates(loadedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('فشل في تحميل قوالب الرسائل');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: WhatsAppTemplateType, field: keyof WhatsAppTemplate, value: any) => {
    setTemplates(prev => ({
      ...prev,
      [templateId]: {
        ...prev[templateId],
        [field]: value
      }
    }));
  };

  const handleSaveTemplate = async (templateId: WhatsAppTemplateType) => {
    try {
      setSaving(true);
      await whatsappTemplatesService.saveTemplate(templates[templateId]);
      setSuccess('تم حفظ القالب بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving template:', error);
      setError('فشل في حفظ القالب');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllTemplates = async () => {
    try {
      setSaving(true);
      await whatsappTemplatesService.saveAllTemplates(templates);
      setSuccess('تم حفظ جميع القوالب بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving all templates:', error);
      setError('فشل في حفظ القوالب');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleResetTemplate = async (templateId: WhatsAppTemplateType) => {
    try {
      setSaving(true);
      await whatsappTemplatesService.resetTemplate(templateId);
      await loadTemplates(); // إعادة تحميل القوالب
      setSuccess('تم إعادة تعيين القالب بنجاح');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error resetting template:', error);
      setError('فشل في إعادة تعيين القالب');
      setTimeout(() => setError(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewTemplate = (templateId: WhatsAppTemplateType) => {
    // بيانات تجريبية للمعاينة
    const sampleVariables = {
      '{customerName}': 'أحمد محمد',
      '{deviceBrand}': 'سامسونج',
      '{deviceSize}': '55 بوصة',
      '{cost}': '500 جنيه',
      '{orderId}': '12345',
      '{receiveDate}': new Date().toLocaleDateString('ar-EG'),
      '{phoneNumber}': '01234567890',
      '{address}': 'الكترونيات السواح - باب اللوق'
    };

    const preview = whatsappTemplatesService.previewTemplate(templates[templateId], sampleVariables);
    setPreviewContent(preview);
    setPreviewDialog(true);
  };

  const handleToggleTemplate = async (templateId: WhatsAppTemplateType, enabled: boolean) => {
    try {
      await whatsappTemplatesService.toggleTemplate(templateId, enabled);
      handleTemplateChange(templateId, 'enabled', enabled);
      setSuccess(`تم ${enabled ? 'تفعيل' : 'إلغاء تفعيل'} القالب`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error toggling template:', error);
      setError('فشل في تغيير حالة القالب');
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>جاري تحميل قوالب الرسائل...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          📝 تحرير قوالب رسائل WhatsApp
        </Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveAllTemplates}
          disabled={saving}
        >
          حفظ جميع القوالب
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {templateKeys.map((templateId, index) => (
              <Tab 
                key={templateId}
                label={templates[templateId]?.name || templateId}
                id={`template-tab-${index}`}
                aria-controls={`template-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {templateKeys.map((templateId, index) => (
          <TabPanel key={templateId} value={activeTab} index={index}>
            <Box>
              {/* Template Header */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {templates[templateId]?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {templates[templateId]?.description}
                  </Typography>
                </Box>
                
                <Box display="flex" gap={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={templates[templateId]?.enabled || false}
                        onChange={(e) => handleToggleTemplate(templateId, e.target.checked)}
                      />
                    }
                    label="مفعل"
                  />
                  
                  <Tooltip title="معاينة">
                    <IconButton onClick={() => handlePreviewTemplate(templateId)}>
                      <PreviewIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="إعادة تعيين">
                    <IconButton onClick={() => handleResetTemplate(templateId)}>
                      <ResetIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Variables */}
              <Box mb={3}>
                <Typography variant="subtitle2" gutterBottom>
                  المتغيرات المتاحة:
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {templates[templateId]?.variables.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>

              {/* Content Editor */}
              <TextField
                fullWidth
                multiline
                rows={12}
                label="محتوى الرسالة"
                value={templates[templateId]?.content || ''}
                onChange={(e) => handleTemplateChange(templateId, 'content', e.target.value)}
                sx={{ mb: 3 }}
                helperText="استخدم المتغيرات المتاحة أعلاه في النص"
              />

              {/* Actions */}
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleSaveTemplate(templateId)}
                  disabled={saving}
                >
                  حفظ القالب
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => handlePreviewTemplate(templateId)}
                >
                  معاينة
                </Button>
              </Box>
            </Box>
          </TabPanel>
        ))}
      </Card>

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialog} 
        onClose={() => setPreviewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            معاينة الرسالة
            <IconButton onClick={() => setPreviewDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                direction: 'rtl'
              }}
            >
              {previewContent}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsAppTemplateEditor;
